import type { ApiErrorBody, ErrorCode, FieldError } from './types';

/**
 * Typed error thrown by the API client. Carries everything the UI needs to map a
 * server failure to the right treatment (plan §4.1 / §5.1) — including request_id,
 * which is surfaced on every error UI for support correlation.
 */
export class AppError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details?: FieldError[];
  readonly requestId?: string;

  constructor(args: {
    code: ErrorCode;
    message: string;
    status: number;
    details?: FieldError[];
    requestId?: string;
  }) {
    super(args.message);
    this.name = 'AppError';
    this.code = args.code;
    this.status = args.status;
    this.details = args.details;
    this.requestId = args.requestId;
  }

  /** Build from a parsed error envelope. */
  static fromEnvelope(body: ApiErrorBody, status: number): AppError {
    return new AppError({
      code: body.error.code,
      message: body.error.message,
      status,
      details: body.error.details,
      requestId: body.error.request_id,
    });
  }

  /** Network / parse failures that never reached an envelope. */
  static network(message = 'Network error. Check your connection and retry.'): AppError {
    return new AppError({ code: 'internal_error', message, status: 0 });
  }

  /** Map validation details to a { field -> message } record for form binding. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const d of this.details ?? []) out[d.field] = d.message;
    return out;
  }

  is(code: ErrorCode): boolean {
    return this.code === code;
  }
}

export function isAppError(e: unknown): e is AppError {
  return e instanceof AppError;
}

/** Whether an error should never be retried by the query layer. */
export function isNonRetryable(e: unknown): boolean {
  if (!isAppError(e)) return false;
  return (
    e.is('validation_error') ||
    e.is('not_found') ||
    e.is('forbidden') ||
    e.is('conflict') ||
    e.is('unsupported_media_type') ||
    e.is('payload_too_large')
  );
}
